import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Clock, Check } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const HOURS = Array.from({ length: 24 }, (_, i) => i);

export default function DigestOptIn() {
  const { user } = useAuth();
  const [pref, setPref] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    http.get("/users/me/news-digest").then((r) => setPref(r.data))
      .catch(() => setPref({ enabled: false, hour: 7, tz_offset_min: -new Date().getTimezoneOffset() }))
      .finally(() => setLoading(false));
  }, [user]);

  if (!user) {
    return (
      <div data-testid="digest-signin-nudge" className="border rounded-lg p-4 mb-6 text-sm flex items-center gap-3" style={{ borderColor: "var(--wofy-line)", background: "rgba(0,0,0,0.03)", color: "var(--wofy-text-soft)" }}>
        <Mail size={16} style={{ color: "var(--wofy-primary)" }} />
        <span><a href="/login?next=/things-to-know" className="underline font-semibold" style={{ color: "var(--wofy-primary)" }}>Sign in</a> to opt into a daily 7 AM email with the Top 10 briefs.</span>
      </div>
    );
  }
  if (loading) return null;
  if (!pref) return null;

  const save = async (next) => {
    setSaving(true);
    try {
      // Auto-detect tz on first opt-in.
      const tz = next.tz_offset_min ?? -new Date().getTimezoneOffset();
      const { data } = await http.patch("/users/me/news-digest", {
        enabled: next.enabled,
        hour: next.hour ?? pref.hour,
        tz_offset_min: tz,
      });
      setPref({ ...pref, ...data.news_digest });
      toast.success(next.enabled ? "Daily digest turned on" : "Daily digest turned off");
    } catch { toast.error("Could not save preference"); }
    finally { setSaving(false); }
  };

  return (
    <div
      data-testid="digest-optin-card"
      className="border rounded-lg p-4 mb-6 grid md:grid-cols-[1fr_auto] gap-3 items-center"
      style={{ borderColor: "var(--wofy-line)", background: pref.enabled ? "rgba(178,93,56,0.06)" : "rgba(0,0,0,0.02)" }}
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5">
          {pref.enabled ? <Check size={18} style={{ color: "#5C6B52" }} /> : <Mail size={18} style={{ color: "var(--wofy-primary)" }} />}
        </div>
        <div>
          <div className="font-semibold text-sm mb-0.5">
            {pref.enabled ? "You’re on the daily digest." : "Get the daily digest by email"}
          </div>
          <div className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>
            The Top 10 briefs for today, delivered to <b>{pref.email}</b> each morning. Free. Turn off any time.
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap justify-end">
        <label className="inline-flex items-center gap-1.5 text-xs" style={{ color: "var(--wofy-text-soft)" }}>
          <Clock size={12} /> Delivery hour (your local time)
          <select
            data-testid="digest-hour-select"
            className="border rounded px-2 py-1 text-xs"
            value={pref.hour}
            disabled={saving}
            onChange={(e) => save({ enabled: pref.enabled, hour: Number(e.target.value) })}
          >
            {HOURS.map((h) => <option key={h} value={h}>{h.toString().padStart(2, "0")}:00</option>)}
          </select>
        </label>
        <button
          data-testid="digest-toggle-btn"
          onClick={() => save({ enabled: !pref.enabled, hour: pref.hour })}
          disabled={saving}
          className="px-4 py-2 rounded-full font-semibold text-sm"
          style={{
            background: pref.enabled ? "transparent" : "var(--wofy-primary)",
            color: pref.enabled ? "var(--wofy-primary)" : "white",
            border: pref.enabled ? "1px solid var(--wofy-primary)" : "none",
          }}
        >
          {pref.enabled ? "Turn off" : "Send me the digest"}
        </button>
      </div>
    </div>
  );
}
